import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

// Gives an element the height left between its own top edge and the bottom of the viewport, so it
// ends flush with the window instead of at a hard-coded pixel cap.
//
// Everything above this element on the grant details page is auto-height (#root is unstyled, so
// html/body's height:100% never reaches any descendant and every height:100% below it resolves to
// auto). Rather than convert that whole chain to flex, this measures the gap and hands back a
// number the caller applies as maxHeight.
//
// The offset is measured against the document, not the viewport, so scrolling doesn't change the
// answer: the element fills the window when the page is scrolled to the top, which is the point at
// which "reaches the bottom" is meaningful.
//
// Recomputes on window resize and on anything that changes the page's layout above the element -
// the grant header accordion collapsing, a task expanding, a phase loading in.

interface FillViewportHeightOptions<T extends HTMLElement> {
    // Space to leave below the element, for page padding or a margin on an ancestor.
    gutter?: number;
    // Never return less than this, however little room is left.
    minHeight?: number;
    // Skip measuring entirely and return undefined, for callers that size themselves some other
    // way in certain modes.
    enabled?: boolean;
    // Checked at measure time, when the DOM is available to look at. Return false to fall back to
    // whatever height the caller uses otherwise - filling the window is only right for an element
    // that has the space below it to itself.
    canFill?: (element: T) => boolean;
}

export default function useFillViewportHeight<T extends HTMLElement>(options: FillViewportHeightOptions<T> = {}) {
    const { gutter = 0, minHeight = 0, enabled = true, canFill } = options;

    const ref = useRef<T>(null);
    const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

    const measure = useCallback(() => {
        const element = ref.current;
        if (!enabled || !element || (canFill && !canFill(element))) {
            setMaxHeight(undefined);
            return;
        }

        // documentElement.clientHeight, not innerHeight: it excludes the horizontal scrollbar, and
        // html carries a permanent vertical one (custom.css).
        const viewport = document.documentElement.clientHeight;
        const documentTop = element.getBoundingClientRect().top + window.scrollY;
        const available = Math.max(viewport - documentTop - gutter, minHeight);

        // Sub-pixel churn here would feed straight back into the observer below.
        setMaxHeight(previous => (previous !== undefined && Math.abs(previous - available) < 1 ? previous : available));
    }, [enabled, gutter, minHeight, canFill]);

    // Layout effect so the first paint is already the right height - a useEffect here showed the
    // element at its natural size for a frame first.
    useLayoutEffect(() => {
        measure();
    }, [measure]);

    useEffect(() => {
        if (!enabled) return;

        window.addEventListener("resize", measure);

        // Watching body rather than the element itself: the element's own height is what this hook
        // sets, so observing it would loop. Body changes whenever something above shifts the
        // element down or up.
        const observer = new ResizeObserver(measure);
        observer.observe(document.body);

        return () => {
            window.removeEventListener("resize", measure);
            observer.disconnect();
        };
    }, [enabled, measure]);

    return { ref, maxHeight, remeasure: measure };
}
